// Persona registry + browser-side client-tool implementations for the Strategic
// Advisor S2S layer. The tool-name KEYS returned by buildClientTools() are a
// hard contract: they must match agent-server/agents.config.js CLIENT_TOOL_SCHEMAS
// exactly, per persona (proven by src/agents/__tests__/personas.contract.test.js).
//
// Memory-backed tools read the Memory Highway through a LIVE getter
// (getMemory: () => MemoryHighway|null), never a captured instance — so they
// always see the current MemoryHighway even after the projects listener
// re-instantiates it, and degrade gracefully (a spoken loading line) when memory
// is not ready yet. Both personas carry the same four knowledge tools.
//
// Every tool is read/delegate-only at this layer:
//   - Advisor: broad READ access over the Memory Highway (STM/PMB/ECB/CL via the
//     user's MemoryHighway instance + a read-only profile fetch) plus delegation
//     (summon_specialist) — never a write.
//   - Engineer: the same project-knowledge READ tools as the advisor, plus
//     dispatch/instruct/interrupt of background Claude Code jobs over the
//     ControlChannel and a handback to the advisor — never resolves a permission
//     (the Allow/Deny gate is click-only) and never writes memory.
//
// Each implementation is wrapped so ANY failure returns the string
// `Error: <message>` rather than throwing — the agent speaks the failure instead
// of stalling the voice loop. All tools resolve to a string (spoken-friendly) and
// none awaits job completion or unbounded network (tool budgets per W9).

import { database } from '../firebase';
import { ref, get } from 'firebase/database';

export const PERSONAS = {
  'strategic-advisor': { displayName: 'Strategic Advisor', voiceLabel: 'Delta' },
  'head-of-engineering': { displayName: 'Head of Engineering', voiceLabel: 'Engineer' },
};

// ---------------------------------------------------------------------------
// Module-scope repo cache: get_repo_status / dispatch_engineering_task resolve a
// repo_name -> path via control.fetchRepos(); cache the list for 60s so repeated
// resolutions inside a conversation stay under the control-read budget.
// ---------------------------------------------------------------------------
const REPO_CACHE_TTL_MS = 60_000;
let _repoCache = { at: 0, repos: null };

// Spoken-friendly stand-in returned by every memory-backed tool when the live
// MemoryHighway instance is not ready yet (Start clicked before memory loads, or
// the projects listener is mid-reinstantiation). The agent says this aloud and
// the user can simply re-ask once memory is ready — no session restart needed.
const MEMORY_LOADING_MSG = 'Memory is still loading — ask me again in a few seconds.';

async function getRepos(control) {
  const now = Date.now();
  if (_repoCache.repos && now - _repoCache.at < REPO_CACHE_TTL_MS) {
    return _repoCache.repos;
  }
  const repos = await control.fetchRepos();
  _repoCache = { at: now, repos };
  return repos;
}

async function resolveRepoPath(control, repoName) {
  const repos = await getRepos(control);
  const match = (repos || []).find((r) => r.name === repoName);
  if (!match) throw new Error(`No repo named "${repoName}".`);
  return match.path;
}

// Wrap an async tool implementation so any thrown error becomes a spoken-friendly
// `Error: <message>` string and the function never rejects.
function safe(fn) {
  return async (params) => {
    try {
      const result = await fn(params || {});
      return typeof result === 'string' ? result : String(result);
    } catch (e) {
      return `Error: ${e?.message || String(e)}`;
    }
  };
}

// ---------------------------------------------------------------------------
// Shared tools (advisor + engineer both expose these by the same names).
// ---------------------------------------------------------------------------
function sharedTools({ control }) {
  return {
    list_repos: safe(async () => {
      const repos = await control.fetchRepos();
      return repos.map((r) => r.name).join(', ');
    }),

    get_repo_status: safe(async (params) => {
      const path = await resolveRepoPath(control, params.repo_name);
      const s = await control.fetchRepoStatus(path);
      return `${s.repo.name} on ${s.branch}, ${s.dirtyFiles} dirty files, last commit ${s.lastCommit}`;
    }),

    list_engineering_jobs: safe(async () => {
      const sessions = await control.list();
      const lines = sessions.map(
        (s) =>
          `${s.label}: ${s.busy ? 'working' : 'idle'}${
            s.pendingPermissions ? ', awaiting your on-screen approval' : ''
          }`
      );
      return lines.join('; ') || 'No active jobs.';
    }),
  };
}

// ---------------------------------------------------------------------------
// Knowledge tools — READ-ONLY over the Memory Highway, shared by BOTH personas.
// Memory is reached through the live getMemory() getter, never a captured
// instance: if memory is not ready the tool returns MEMORY_LOADING_MSG so the
// agent speaks it instead of erroring out the voice loop.
// ---------------------------------------------------------------------------
function knowledgeTools({ getMemory, getProjects }) {
  const mem = () => (getMemory ? getMemory() : null);
  return {
    // STM is in-memory — no awaited network (under the 250ms tool budget).
    get_portfolio_overview: safe(async () => {
      const m = mem();
      if (!m) return MEMORY_LOADING_MSG;
      return JSON.stringify(m.buildContextForAI());
    }),

    list_projects: safe(async () =>
      JSON.stringify(
        getProjects().map((p) => ({
          name: p.name,
          goals: (p.goals || []).length,
          goalsDone: (p.goals || []).filter((g) => g.completed).length,
          tasks: (p.todos || []).length,
        }))
      )
    ),

    get_project_knowledge: safe(async (params) => {
      const m = mem();
      if (!m) return MEMORY_LOADING_MSG;
      return m.getProjectKnowledge(params.project_name).summary;
    }),

    get_recent_conversation: safe(async (params) => {
      const m = mem();
      if (!m) return MEMORY_LOADING_MSG;
      return (
        m.CL.slice(-(params.limit || 10))
          .map((c) => `${c.role}: ${c.content}`)
          .join('\n') || 'No conversation history yet.'
      );
    }),
  };
}

// ---------------------------------------------------------------------------
// Advisor-only tools — the shared knowledge tools + a read-only profile fetch +
// delegation. READ-ONLY over the Memory Highway.
// ---------------------------------------------------------------------------
function advisorTools({ userId, getMemory, getProjects, control, callbacks }) {
  return {
    ...knowledgeTools({ getMemory, getProjects }),

    // READ only: the signed-in user's profile node.
    get_user_profile: safe(async () => {
      const snap = await get(ref(database, 'users/' + userId + '/profile'));
      return JSON.stringify(snap.val() || {});
    }),

    summon_specialist: safe(async (params) => {
      callbacks.onSummon(params.specialist, params.briefing);
      return 'Bringing in the Head of Engineering now.';
    }),

    get_session_cost: safe(async () => {
      const u = await control.fetchUsage();
      const cost =
        typeof u.costTodayUsd === 'number'
          ? `About $${u.costTodayUsd.toFixed(2)} on ElevenLabs today across ${u.minutesToday ?? '?'} minutes. `
          : '';
      return `${cost}${u.sessionsToday} of ${u.dailyCap} voice sessions used today.`;
    }),

    dispatch_research_task: safe(async (p) => {
      await control.dispatchResearch(p.subject, p.query);
      return `Researcher dispatched on ${p.subject}.`;
    }),

    ...sharedTools({ control }),
  };
}

// ---------------------------------------------------------------------------
// Engineer-only tools — the same project-knowledge READ tools as the advisor
// (so the briefed specialist can answer project questions immediately) plus
// dispatch/instruct/interrupt of background jobs + handback.
// dispatch_engineering_task resolves on the 'dispatched' ACK (never completion),
// so the mic stays live while the job runs.
// ---------------------------------------------------------------------------
function engineerTools({ getMemory, getProjects, control, callbacks }) {
  // Find a session by its label or sessionId from the last control.list().
  const findJob = async (job) => {
    const sessions = await control.list();
    const match = sessions.find((s) => s.label === job || s.sessionId === job);
    if (!match) throw new Error(`No job called "${job}".`);
    return match;
  };

  return {
    dispatch_engineering_task: safe(async (params) => {
      const repoPath = await resolveRepoPath(control, params.repo_name);
      const { label } = await control.dispatch({
        repoPath,
        instruction: params.instruction,
        label: params.label,
      });
      return `Dispatched job ${label}. It runs in the background — I will report progress.`;
    }),

    check_job: safe(async (params) => {
      const s = await findJob(params.job);
      return `${s.label}: ${s.busy ? 'working' : 'idle'}${
        s.pendingPermissions ? ', awaiting your on-screen approval' : ''
      }.`;
    }),

    instruct_job: safe(async (params) => {
      const s = await findJob(params.job);
      control.say(s.sessionId, params.text);
      return 'Sent.';
    }),

    interrupt_job: safe(async (params) => {
      const s = await findJob(params.job);
      control.interrupt(s.sessionId);
      return 'Interrupted.';
    }),

    return_to_advisor: safe(async (params) => {
      callbacks.onReturn(params.summary);
      return 'Handing you back to the Strategic Advisor.';
    }),

    ...knowledgeTools({ getMemory, getProjects }),

    ...sharedTools({ control }),
  };
}

// buildClientTools — returns { [toolName]: async (params) => string } for the
// requested persona. The KEYS are the W6 contract (enforced by the parity test).
// getMemory is a live getter (() => MemoryHighway|null) forwarded to both persona
// builders so tools never close over a stale/null instance.
export function buildClientTools({ persona, userId, getMemory, getProjects, control, callbacks }) {
  if (persona === 'strategic-advisor') {
    return advisorTools({ userId, getMemory, getProjects, control, callbacks });
  }
  if (persona === 'head-of-engineering') {
    return engineerTools({ getMemory, getProjects, control, callbacks });
  }
  throw new Error(`Unknown persona: ${persona}`);
}

// warmRepoCache — fire-and-forget pre-population of the module-scope 60s repo
// cache so the first get_repo_status / dispatch_engineering_task path resolution
// is instant. Never rejects (callers fire-and-forget at connect time).
export function warmRepoCache(control) {
  return getRepos(control).catch(() => {});
}
